import type { ClientPositionView } from "@solomon-apps/shared";

import { type Holding, holdingsByPositionUuid } from "@/pages/Positions/holdings";
import type { DistributionItem } from "@/services/clientPositions";

export interface ReportingDistribution {
    uuid: string;
    timestampMs: number;
    sourceLabel: string;
    byPosition: Array<{
        positionUuid: string;
        positionName: string;
        amountUsdv: number;
        sourceWallet: string;
        destinationWallet: string;
        txSignature: string;
    }>;
}

export interface ReportingDistributionSeries {
    distributions: ReportingDistribution[];
    positions: Array<{ uuid: string; name: string }>;
}

function timestampMs(value: string): number {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

/* A payout groups under its holding (every Program Position on one wallet or
 * LP position reads as one), named as the Positions page names it. Payouts
 * without a known position fall back to their own name. */
function positionKey(item: DistributionItem, holding: Holding | undefined): string {
    return holding?.key ?? item.positionUuid ?? `pool:${item.pool}`;
}

function positionName(item: DistributionItem, holding: Holding | undefined): string {
    return holding?.name ?? item.positionName ?? item.pool;
}

export function buildReportingDistributionSeries(
    items: DistributionItem[],
    positions: readonly ClientPositionView[] = []
): ReportingDistributionSeries {
    const holdings = holdingsByPositionUuid(positions);
    const grouped = new Map<number, ReportingDistribution>();
    const positionsByUuid = new Map<string, { uuid: string; name: string }>();

    for (const item of items) {
        const ts = timestampMs(item.distributionDate);
        const holding = item.positionUuid ? holdings.get(item.positionUuid) : undefined;
        const key = positionKey(item, holding);
        const name = positionName(item, holding);
        positionsByUuid.set(key, { uuid: key, name });

        const row =
            grouped.get(ts) ??
            ({
                uuid: `distribution-${ts}`,
                timestampMs: ts,
                sourceLabel: name,
                byPosition: []
            } satisfies ReportingDistribution);

        const existing = row.byPosition.find((entry) => entry.positionUuid === key);
        if (existing) {
            existing.amountUsdv += item.amountUsdv;
        } else {
            row.byPosition.push({
                positionUuid: key,
                positionName: name,
                amountUsdv: item.amountUsdv,
                sourceWallet: item.sourceWallet,
                destinationWallet: item.destinationWallet,
                txSignature: item.txSignature
            });
        }

        row.sourceLabel = Array.from(new Set(row.byPosition.map((entry) => entry.positionName))).join(" + ");
        grouped.set(ts, row);
    }

    return {
        distributions: Array.from(grouped.values()).sort((a, b) => a.timestampMs - b.timestampMs),
        positions: Array.from(positionsByUuid.values())
    };
}
